<template>
  <div class="komposisi">
    <v-snackbar :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
      {{ snackbar.message }}
    </v-snackbar>

    <PageHeader
      icon="mdi-chart-arc"
      title="Komposisi Kumpulan Utama Perbelanjaan Isi Rumah Bulanan"
      subtitle="Perbelanjaan penggunaan dan bukan penggunaan mengikut strata"
    />

    <v-card outlined class="mb-4">
      <v-card-title class="py-3 d-flex align-center flex-wrap">
        <v-icon small class="mr-2">mdi-table</v-icon>
        <span class="subtitle-1 font-weight-medium">Komposisi Kumpulan Utama Perbelanjaan Isi Rumah Bulanan</span>
        <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ scopeName }}</v-chip>
        <v-spacer></v-spacer>
        <v-btn color="green darken-2" dark class="mr-2 text-none" :disabled="!hasData" :loading="exporting" @click="exportExcel">
          <v-icon left>mdi-microsoft-excel</v-icon>Excel
        </v-btn>
        <v-btn color="red darken-1" dark class="text-none" :disabled="!hasData" :loading="exportingPdf" @click="exportPDF">
          <v-icon left>mdi-file-pdf-box</v-icon>PDF
        </v-btn>
      </v-card-title>

      <!-- Setiap penapis memakai susunan yang SAMA: label eyebrow di atas,
           kawalan di bawah. `align="end"` menjajarkan semua kawalan pada garis
           dasar yang sama walaupun tinggi labelnya berbeza. -->
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <v-col cols="12" sm="3">
            <div class="fld__label">Negeri</div>
            <v-select v-model="negeri" :items="negeriList"
              :disabled="negeriLocked"
              outlined dense hide-details></v-select>
          </v-col>
          <v-col cols="12" sm="auto">
            <div class="fld__label">Pusingan</div>
            <div class="d-flex align-center pusingan-range">
              <v-select v-model="roundRange" :items="roundRangeItems" outlined dense hide-details class="pusingan-sel"></v-select>
            </div>
          </v-col>
          <v-col cols="12" sm="auto">
            <div class="fld__label">Data</div>
            <!-- `color="primary"` digugurkan: primary Vuetify = biru, yang
                 bertentangan dengan sistem teal. Keadaan aktif diwarnakan
                 melalui CSS (.data-toggle) sebaliknya. -->
            <!-- 38, bukan 40: + birai 1px atas/bawah = tepat 40px, sama tinggi
                 dengan medan outlined di sebelahnya, supaya label "DATA" duduk
                 pada garis yang sama dengan "NEGERI"/"PUSINGAN". -->
            <v-btn-toggle v-model="dataWajaran" mandatory class="data-toggle">
              <v-btn value="W" height="38" class="text-none px-4">Wajaran</v-btn>
              <v-btn value="TW" height="38" class="text-none px-4">Tanpa Wajaran</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>Tetapkan Semula
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="d-flex align-center flex-wrap py-2">
        <v-spacer></v-spacer>
        <span class="penapis-ringkas text--secondary">{{ filterSummary }}</span>
      </v-card-text>

      <div v-if="loading" class="empty-state text-center py-12 px-4">
        <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
        <div class="subtitle-1 font-weight-medium grey--text text--darken-1 mt-3">Memuatkan data…</div>
      </div>
      <div v-else-if="!hasData" class="empty-state text-center py-12 px-4">
        <v-icon size="56" color="grey lighten-1">mdi-database-off-outline</v-icon>
        <div class="subtitle-1 font-weight-medium grey--text text--darken-1 mt-3">Tiada rekod dijumpai</div>
        <div class="penapis-ringkas grey--text mt-1">Tiada data bagi penapis semasa ({{ filterSummary }}). Sila ubah pilihan penapis.</div>
      </div>

      <template v-else>
      <v-simple-table class="kmp-table">
        <thead>
          <tr>
            <th class="cat" :rowspan="showStrata ? 3 : 2">Kod 2D</th>
            <th class="cat ket" :rowspan="showStrata ? 3 : 2">Kumpulan Utama Perbelanjaan</th>
            <th v-for="y in YEARS" :key="'y' + y" :colspan="colsPerYear" class="yr">{{ y }}</th>
          </tr>
          <tr v-if="showStrata">
            <template v-for="y in YEARS">
              <th v-for="st in strataCols" :key="'s' + y + st" :colspan="2" class="sub2">{{ st }}</th>
            </template>
          </tr>
          <tr>
            <template v-for="y in YEARS">
              <template v-for="st in strataCols">
                <th :key="'rm' + y + st" class="sub3">Purata (RM)</th>
                <th :key="'pct' + y + st" class="sub3">Peratus (%)</th>
              </template>
            </template>
          </tr>
        </thead>
        <tbody>
          <!-- keyed by index: kod is blank on the Jumlah rows and 01-13 appears twice -->
          <tr v-for="(r, i) in rows" :key="i" :class="{ total: r.isTotal, sub: !r.isTotal }">
            <!-- Jumlah rows have no kod: the label spans both label columns -->
            <td v-if="r.kod" class="cat kod">{{ r.kod }}</td>
            <td class="cat ket" :colspan="r.kod ? 1 : 2">{{ r.keterangan }}</td>
            <td v-for="c in dataCells(r)" :key="c.key" :class="{ pct: c.pct }">{{ c.text }}</td>
          </tr>
        </tbody>
      </v-simple-table>

      <div class="foot penapis-ringkas text--secondary px-4 py-3 d-flex justify-space-between flex-wrap">
        <span>{{ scopeName }}</span>
        <span>{{ filterSummary }}</span>
      </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { addJataHeaderExcel, addJataHeaderPdf } from "@/services/exportHeader";
import { T, loadTheme, argb, rgb, dark } from "@/services/theme";
import hiesWatermark from "@/assets/HIES watermark.png";
import PerbelanjaanKomposisiServices from "@/services/perbelanjaanKomposisi";
import { surveyTitle } from "@/services/surveyYear";
import { negeriStore, loadNegeriList } from "@/services/negeriList";
import getPusingan from "@/services/pusingan";
import { negeriLock } from "@/services/negeriScope";

const svc = new PerbelanjaanKomposisiServices();
const pusinganSvc = new getPusingan();

// Negeri dropdown labels come from lookupnegeri via the shared negeriList
// service (negeriStore.list — reactive, cached, with offline fallback).
const STRATA = ["Bandar", "Luar Bandar", "Jumlah"];

function colLetter(n) {
  let s = ""; while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); } return s;
}
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve();
    const el = document.createElement("script");
    el.src = src; el.onload = () => resolve(); el.onerror = () => reject(new Error("Gagal memuatkan " + src));
    document.head.appendChild(el);
  });
}
// load an image asset → { dataUrl(PNG base64), width, height }
// alpha (0-1, optional): bake transparency into the pixels — used for watermarks,
// since jsPDF's addImage has no opacity option of its own
function loadImage(url, alpha) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      const c2d = canvas.getContext("2d");
      if (alpha != null) c2d.globalAlpha = alpha;
      c2d.drawImage(img, 0, 0);
      let dataUrl; try { dataUrl = canvas.toDataURL("image/png"); } catch (e) { dataUrl = url; }
      resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject; img.src = url;
  });
}

export default {
  name: "PerbelanjaanKomposisi",
  data() {
    return {
      svc,
      years: [],
      api: {},
      loading: true,   // start loading — page fetches on created()
      snackbar: { show: false, message: "", color: "success", timeout: 3000 },
      negeri: "ALL",
      negeriLocked: false,    // true for PP/PO/FE → negeri pinned to own kodneg
      sampel: "3",
      dataWajaran: "W",
      roundItems: [],
      roundFrom: null,
      roundTo: null,
      exporting: false,
      exportingPdf: false,
    };
  },
  computed: {
    todayStr() { return new Date().toISOString().slice(0, 10); },
    YEARS() { return this.years; },
    rows() { return this.api.rows || []; },
    hasData() { return this.years.length > 0 && this.rows.length > 0; },
    // Bandar/Luar Bandar are Malaysia-only (the backend's strata tables carry no per-negeri
    // split), so with a negeri selected those two columns are dashes end to end. Detect that
    // from the payload rather than from `negeri`, so the layout always matches the rows on
    // screen even while a refetch is in flight, then show the Jumlah figures alone.
    strataCols() {
      const split = STRATA.filter(st => st !== "Jumlah");
      const anySplit = split.some(st =>
        this.rows.some(r => this.years.some(y => r.strata && r.strata[st] && r.strata[st][y] != null)));
      return anySplit ? STRATA : ["Jumlah"];
    },
    // One strata left → its header row labels nothing, so it is dropped entirely.
    showStrata() { return this.strataCols.length > 1; },
    colsPerYear() { return this.strataCols.length * 2; },
    // Kod + Keterangan + (Purata/Peratus per strata, per year)
    colCount() { return 2 + this.years.length * this.colsPerYear; },
    negeriList() {
      // Locked roles (PP/PO/FE) only see their own negeri — no Malaysia/other options.
      if (this.negeriLocked) {
        return negeriStore.list.filter(n => n.id === this.negeri).map(n => ({ text: n.nama, value: n.id }));
      }
      return [{ text: "Malaysia", value: "ALL" }, ...negeriStore.list.map(n => ({ text: n.nama, value: n.id }))];
    },
    negeriName() { const m = {}; negeriStore.list.forEach(n => (m[n.id] = n.nama)); return m; },
    scopeName() { return this.negeri === "ALL" ? "Malaysia" : (this.negeriName[this.negeri] || this.negeri); },
    selectedRoundsModel() {
      if (this.roundFrom == null || this.roundTo == null) return [];
      const lo = Math.min(this.roundFrom, this.roundTo), hi = Math.max(this.roundFrom, this.roundTo);
      return this.roundItems.filter(p => p >= lo && p <= hi);
    },
    selectedRounds() { return [...this.selectedRoundsModel].sort((a, b) => a - b); },
    allRounds() { return this.roundItems.length > 0 && this.selectedRoundsModel.length === this.roundItems.length; },
    // Pusingan sentiasa julat berlabuh pada pusingan pertama — pengguna hanya
    // memilih hujungnya (01 - 01 … 01 - 12), jadi permulaan 02 mustahil dari binaan.
    roundRangeItems() {
      const pad = n => String(n).padStart(2, "0");
      const lo = this.roundItems.length ? this.roundItems[0] : null;
      return this.roundItems.map(p => ({ text: `${pad(lo)} - ${pad(p)}`, value: p }));
    },
    // One dropdown, so the start can never drift off the first pusingan.
    roundRange: {
      get() { return this.roundTo; },
      set(v) {
        if (this.roundItems.length) this.roundFrom = this.roundItems[0];
        this.roundTo = v;
      },
    },
    ddLabel() {
      if (this.allRounds) return "Semua Pusingan";
      if (!this.selectedRoundsModel.length) return "Tiada dipilih";
      const lo = this.selectedRounds[0], hi = this.selectedRounds[this.selectedRounds.length - 1];
      return lo === hi ? `Pusingan ${lo}` : `Pusingan ${lo}–${hi}`;
    },
    wajaranLabel() { return this.dataWajaran === "TW" ? "Tanpa Wajaran" : "Wajaran"; },
    pusinganSlug() {
      const rounds = this.selectedRounds.length ? this.selectedRounds : this.roundItems;
      if (!rounds.length) return "Pusingan";
      const lo = rounds[0], hi = rounds[rounds.length - 1];
      return "Pusingan" + (lo === hi ? `${lo}` : `${lo}-${hi}`);
    },
    exportFileName() {
      const parts = ["Komposisi_Perbelanjaan_Penggunaan"];
      if (this.negeri !== "ALL") parts.push((this.scopeName || "").replace(/[^A-Za-z0-9]+/g, ""));
      parts.push(this.pusinganSlug);
      parts.push(this.dataWajaran === "TW" ? "TanpaWajaran" : "Wajaran");
      parts.push(this.todayStr.replace(/-/g, ""));
      return parts.join("_");
    },
    filterSummary() {
      const rd = this.allRounds ? "Semua pusingan" : "Pusingan " + this.selectedRounds.join(",");
      return `${this.scopeName} • ${rd} • ${this.wajaranLabel}`;
    },
  },
  watch: {
    negeri() { this.queueFetch(); },
    dataWajaran() { this.queueFetch(); },
    roundFrom(v) { if (v != null && this.roundTo != null && this.roundTo < v) this.roundTo = v; },
    selectedRoundsModel() { this.queueFetch(); },
  },
  async created() {
    loadNegeriList(); // refresh negeri names from lookupnegeri (shared cache)
    // Restricted roles (PP/PO/FE) are pinned to their own negeri — lock the selector
    // and default it to their kodneg before the first fetch. (The backend enforces
    // this too, so the data is scoped regardless; this is the matching UI.)
    const lock = negeriLock(this.$store);
    if (lock.locked) {
      this.negeri = lock.kodneg;
      this.negeriLocked = true;
    }
    await this.loadActiveRounds();
    this.fetchData();
  },
  methods: {
    queueFetch() { clearTimeout(this._fetchTimer); this._fetchTimer = setTimeout(() => this.fetchData(), 0); },
    async fetchData() {
      this.loading = true;
      try {
        const P = this.selectedRoundsModel.length === 0 ? "__NONE__" : this.selectedRounds.join(",");
        const params = { P, S: this.sampel, W: this.dataWajaran };
        if (this.negeri !== "ALL") params.NG = this.negeri;
        const d = await this.svc.getPerbelanjaanKomposisi(params);
        this.years = (d.years || []).map(Number);
        this.api = d;
      } catch (err) {
        this.api = {}; this.years = [];
      }
      this.loading = false;
    },
    rm(n) {
      if (n === null || n === undefined) return "—";
      return new Intl.NumberFormat("en-MY").format(Math.round(n));
    },
    pf(n) {
      if (n === null || n === undefined) return "—";
      return n.toFixed(1);
    },
    // flat cell list per row: for each year × strata → Purata (RM) then Peratus (%)
    dataCells(r) {
      const cells = [];
      this.years.forEach(y => {
        this.strataCols.forEach(st => {
          const c = r.strata[st] ? r.strata[st][y] : null;
          cells.push({ key: "rm" + y + st, text: this.rm(c ? c.rm : null) });
          cells.push({ key: "pct" + y + st, text: this.pf(c ? c.pct : null), pct: true });
        });
      });
      return cells;
    },
    // Locked roles (PP/PO/FE) stay pinned to their own negeri — "ALL" is not in
    // their negeriList, so resetting to it would blank the select and mislabel
    // the scope (chip/exports would read "Malaysia").
    resetFilters() {
      if (!this.negeriLocked) this.negeri = "ALL";
      this.sampel = "3";
      this.selectFullRange();
    },
    selectFullRange() {
      if (!this.roundItems.length) return;
      this.roundFrom = this.roundItems[0];
      this.roundTo = this.roundItems[this.roundItems.length - 1];
    },
    async loadActiveRounds() {
      let rounds = Array.from({ length: 12 }, (_, i) => i + 1);
      try {
        const res = await pusinganSvc.getAllPusingan();
        const list = (res && res.data && res.data.data) || [];
        const active = list.filter(r => r.status == "1" || r.status == 1).map(r => parseInt(r.pusingan, 10)).filter(n => !isNaN(n));
        const uniq = [...new Set(active)].sort((a, b) => a - b);
        if (uniq.length) rounds = uniq;
      } catch (e) { console.error("loadActiveRounds failed:", e); }
      this.roundItems = rounds;
      this.selectFullRange();
    },

    /* ---- styled .xlsx export ---- */
    async exportExcel() {
      this.exporting = true;
      try {
        await loadTheme();
        await loadScript("/js/exceljs.min.js");
        const ExcelJS = window.ExcelJS;
        const NAVY = argb(T.tajukLaporan), HEAD = argb(T.kepalaJadual), NAVY2 = argb(dark(T.kepalaJadual)), TEAL = argb(T.graf), WHITE = "FFFFFFFF",
              SUB = "FFF4F8FA", LINE = "FFE2E8F0";
        const thin = { style: "thin", color: { argb: LINE } };
        const border = { top: thin, left: thin, bottom: thin, right: thin };
        const center = { vertical: "middle", horizontal: "center", wrapText: true };
        const fill = argb => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

        const YEARS = this.years, L = colLetter;
        // SC/showStrata: a selected negeri has Jumlah only — see the strataCols computed.
        const SC = this.strataCols, showStrata = this.showStrata, perYear = SC.length * 2;
        const nCols = 2 + YEARS.length * perYear;
        const last = L(nCols);
        const bannerCols = Math.max(nCols, 6);
        const bannerLast = L(bannerCols);

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Komposisi", { views: [{ showGridLines: false }] });
        const widths = [{ width: 20 }, { width: 46 }, ...Array(YEARS.length * perYear).fill({ width: 13 })];
        while (widths.length < bannerCols) widths.push({ width: 12 });
        ws.columns = widths;

        // ── Logo row 1: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderExcel(wb, ws, widths, bannerLast);

        // ── Title block (rows 2-3, below the logo) ───────────────────────────
        ws.mergeCells(`A2:${bannerLast}2`);
        Object.assign(ws.getCell("A2"), { value: await surveyTitle(), alignment: center, font: { bold: true, size: 12, color: { argb: WHITE } }, fill: fill(NAVY) });
        ws.getRow(2).height = 24;

        ws.mergeCells(`A3:${bannerLast}3`);
        Object.assign(ws.getCell("A3"), { value: "KOMPOSISI 2 DIGIT PERBELANJAAN PENGGUNAAN DAN BUKAN PENGGUNAAN ISI RUMAH BULANAN", alignment: center, font: { bold: true, size: 11, color: { argb: WHITE } }, fill: fill(TEAL) });
        ws.getRow(3).height = 20;

        ws.mergeCells(`A5:${bannerLast}5`);
        Object.assign(ws.getCell("A5"), { value: `Pilihan: ${this.scopeName}     |     Pusingan: ${this.ddLabel}     |     Data: ${this.wajaranLabel}`, font: { size: 10 }, alignment: { vertical: "middle", horizontal: "left" } });
        ws.getRow(5).height = 18;

        const setH = (cell, text, bg) => Object.assign(ws.getCell(cell), { value: text, alignment: center, border, font: { bold: true, size: 9.5, color: { argb: WHITE } }, fill: fill(bg || HEAD) });
        // H1 = year. H2 = strata, present only when there is more than one. H3 = the
        // Purata/Peratus row, which moves up to 8 when the strata row is dropped.
        const H1 = 7, H2 = 8, H3 = showStrata ? 9 : 8;
        ws.mergeCells(`A${H1}:A${H3}`); setH(`A${H1}`, "KOD 2D");
        ws.mergeCells(`B${H1}:B${H3}`); setH(`B${H1}`, "KUMPULAN UTAMA PERBELANJAAN");
        YEARS.forEach((y, yi) => {
          const base = 3 + yi * perYear;            // 1-based first col for this year
          ws.mergeCells(`${L(base)}${H1}:${L(base + perYear - 1)}${H1}`); setH(`${L(base)}${H1}`, String(y));
          SC.forEach((st, si) => {
            const c0 = base + si * 2;
            if (showStrata) { ws.mergeCells(`${L(c0)}${H2}:${L(c0 + 1)}${H2}`); setH(`${L(c0)}${H2}`, st, NAVY2); }
            setH(`${L(c0)}${H3}`, "Purata (RM)", NAVY2);
            setH(`${L(c0 + 1)}${H3}`, "Peratus (%)", NAVY2);
          });
        });
        ws.getRow(H1).height = 20; if (showStrata) ws.getRow(H2).height = 18; ws.getRow(H3).height = 26;

        let rowIdx = H3 + 1;
        this.rows.forEach(r => {
          const row = ws.getRow(rowIdx);
          // Jumlah rows carry no kod → A:B merge below. The merge keeps the top-left
          // value, so the label has to sit in A rather than B on those rows.
          const merged = !r.kod;
          const vals = merged ? [r.keterangan, null] : [r.kod, r.keterangan];
          YEARS.forEach(y => {
            SC.forEach(st => {
              const c = r.strata[st] ? r.strata[st][y] : null;
              vals.push(c && c.rm != null ? Math.round(c.rm) : null);
              vals.push(c && c.pct != null ? c.pct : null);
            });
          });
          vals.forEach((val, ci) => {
            const cell = row.getCell(ci + 1);
            const isPct = ci >= 2 && (ci - 2) % 2 === 1;
            if (ci >= 2 && (val === null || val === undefined)) cell.value = "—";
            else cell.value = val;
            cell.border = border;
            cell.alignment = ci <= 1 ? { vertical: "middle", horizontal: "left", wrapText: ci === (merged ? 0 : 1) } : { vertical: "middle", horizontal: "right" };
            cell.font = { size: 10, bold: !!r.isTotal || ci === 0 };
            if (ci >= 2 && typeof cell.value === "number") cell.numFmt = isPct ? "0.0" : "#,##0";
          });
          if (r.isTotal) for (let c = 1; c <= nCols; c++) { row.getCell(c).fill = fill(TEAL); row.getCell(c).font = { size: 10, bold: true, color: { argb: WHITE } }; }
          else for (let c = 1; c <= nCols; c++) row.getCell(c).fill = fill(SUB);
          if (merged) ws.mergeCells(`A${rowIdx}:B${rowIdx}`);   // after the fills, so B is styled too
          rowIdx++;
        });

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${this.exportFileName}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport Excel gagal.", color: "error", timeout: 4000 };
      }
      this.exporting = false;
    },

    /* ---- styled PDF export ---- */
    async exportPDF() {
      this.exportingPdf = true;
      try {
        await loadTheme();
        await loadScript("/js/jspdf.umd.min.js");
        await loadScript("/js/jspdf.plugin.autotable.min.js");
        const { jsPDF } = window.jspdf;
        const NAVY = rgb(T.tajukLaporan), HEAD = rgb(T.kepalaJadual), NAVY2 = rgb(dark(T.kepalaJadual)), TEAL = rgb(T.graf);

        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // ── Watermark (faded HIES logo) — painted FIRST so the title and table
        //    render on top of it. jsPDF has no z-order: paint order is stacking
        //    order, so "behind" = drawn before. 0.14 keeps the blue/green visible.
        const wm = await loadImage(hiesWatermark, 0.14);
        const wmH = 110; // mm
        const wmW = wm.width * (wmH / wm.height);
        const drawWatermark = () =>
          doc.addImage(wm.dataUrl, "PNG", (pageW - wmW) / 2, (pageH - wmH) / 2, wmW, wmH);
        drawWatermark(); // page 1 — later pages via autoTable's willDrawPage hook

        // ── Logo: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderPdf(doc, pageW);

        doc.setFont("times", "bold"); doc.setFontSize(12); doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.text(await surveyTitle(), pageW / 2, 35, { align: "center" });
        doc.setFontSize(9.5); doc.setTextColor(TEAL[0], TEAL[1], TEAL[2]);
        doc.text("KOMPOSISI 2 DIGIT PERBELANJAAN PENGGUNAAN DAN BUKAN PENGGUNAAN ISI RUMAH BULANAN", pageW / 2, 41.5, { align: "center" });
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(90);
        doc.text(`Pilihan: ${this.scopeName}     |     Pusingan: ${this.ddLabel}     |     Data: ${this.wajaranLabel}     |     Dijana: ${new Date().toLocaleString("en-MY")}`, 14, 48);

        const YEARS = this.years;
        // SC/showStrata: a selected negeri has Jumlah only — see the strataCols computed.
        const SC = this.strataCols, showStrata = this.showStrata, perYear = SC.length * 2;
        const headRows = showStrata ? 3 : 2;
        const head = [
          [{ content: "Kod 2D", rowSpan: headRows }, { content: "Kumpulan Utama Perbelanjaan", rowSpan: headRows }, ...YEARS.map(y => ({ content: String(y), colSpan: perYear }))],
          ...(showStrata ? [YEARS.flatMap(() => SC.map(st => ({ content: st, colSpan: 2 })))] : []),
          YEARS.flatMap(() => SC.flatMap(() => ["Purata (RM)", "Peratus (%)"])),
        ];
        const kinds = [];
        const body = this.rows.map(r => {
          kinds.push(r.isTotal ? "total" : "sub");
          // Jumlah rows have no kod → the label spans both label columns. The spanning
          // cell sits at column 0, so it needs its own halign (didParseCell's left-align
          // only fires for column 1).
          const cells = r.kod
            ? [r.kod, r.keterangan]
            : [{ content: r.keterangan, colSpan: 2, styles: { halign: "left" } }];
          YEARS.forEach(y => SC.forEach(st => {
            const c = r.strata[st] ? r.strata[st][y] : null;
            cells.push(this.rm(c ? c.rm : null));
            cells.push(this.pf(c ? c.pct : null));
          }));
          return cells;
        });

        doc.autoTable({
          head, body, startY: 52,
          // pages the table adds get their watermark painted before their content
          // too (page 1 already has it — drawn above, before the header block)
          willDrawPage: d => { if (d.pageNumber > 1) drawWatermark(); },
          // fillColor:false = TRANSPARENT body cells, so the watermark painted
          // beneath the table shows through (the default theme paints every cell
          // white, which was hiding it). Meaningful fills (totals) still apply.
          theme: "plain",
          styles: { font: "helvetica", fontSize: 6, cellPadding: 1, halign: "right", lineColor: [226, 232, 240], lineWidth: 0.1, valign: "middle", fillColor: false },
          headStyles: { fillColor: HEAD, textColor: 255, halign: "center", fontSize: 5.8, valign: "middle" },
          columnStyles: { 0: { halign: "center", cellWidth: 18 }, 1: { halign: "left", cellWidth: 44, fontSize: 5.6 } },
          didParseCell: d => {
            // columnStyles outranks headStyles in autoTable, so column 1's header would
            // inherit its body halign:left — re-centre every header here instead.
            if (d.section === "head") d.cell.styles.halign = "center";
            if (d.section === "head" && d.row.index >= 1) d.cell.styles.fillColor = NAVY2;
            if (d.section === "body") {
              const kind = kinds[d.row.index];
              if (kind === "total") { d.cell.styles.fillColor = TEAL; d.cell.styles.textColor = 255; d.cell.styles.fontStyle = "bold"; }
              // non-total rows: no fill — transparent so the watermark shows through
              if (d.column.index === 1) d.cell.styles.halign = "left";
            }
          },
        });

        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120);
        for (let i = 1; i <= pageCount; i++) { doc.setPage(i); doc.text(`${i}/${pageCount}`, pageW - 14, pageH - 6, { align: "right" }); }
        doc.save(`${this.exportFileName}.pdf`);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport PDF gagal.", color: "error", timeout: 4000 };
      }
      this.exportingPdf = false;
    },
  },
};
</script>

<style scoped>
/* ---------- Bar penapis ----------
   Warna daripada palet yang dikongsi `paparanutama`, sidebar (MenuHeader) dan
   <PageHeader>: teal-700 #037769, wash #eef8f6, hair #dbeeeb. */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}

/* Label penapis — sama gaya dengan `.nav-eyebrow` dalam sidebar. */
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}

/* Medan outlined: birai teal lembut, menyerlah teal-700 bila fokus. */
.filter-bar ::v-deep .v-input fieldset {
  border-color: #cfeae7;
}
.filter-bar ::v-deep .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar ::v-deep .v-select__selection--comma {
  color: #21403c;
}
.filter-bar ::v-deep .v-input__append-inner .v-icon {
  color: #7aa8a2;
}

/* Butang "Tetapkan Semula" — dulu kelabu neutral; kini senada teal. */
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}

.pusingan-range .pusingan-sel { width: 84px; flex: 0 0 84px; }
.pusingan-range ::v-deep .v-select__selections { flex-wrap: nowrap; }
.pusingan-range ::v-deep .v-input__slot { padding: 0 8px !important; }
.pusingan-range ::v-deep .v-input__append-inner { margin-left: 0; }

.komposisi { padding: 16px; margin-left: -12px; margin-right: -12px; }
/* Toggle Wajaran / Tanpa Wajaran: birai sepadan dengan medan outlined di
   sebelahnya; pilihan aktif = teal pejal (dulu biru `primary` Vuetify). */
.data-toggle {
  border: 1px solid #cfeae7;
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}
.data-toggle ::v-deep .v-btn {
  border: none !important;
  letter-spacing: normal;
  color: #4a8a84;
  background: transparent;
}
.data-toggle ::v-deep .v-btn.v-btn--active {
  background: #037769 !important;
  color: #ffffff !important;
}
/* Vuetify meletakkan lapisan legap pada butang aktif — ia mengaburkan teal. */
.data-toggle ::v-deep .v-btn.v-btn--active .v-btn__overlay,
.data-toggle ::v-deep .v-btn.v-btn--active::before {
  opacity: 0 !important;
}

::v-deep .kmp-table > .v-data-table__wrapper { overflow-x: auto; }
::v-deep .kmp-table table { border-collapse: separate; border-spacing: 0; width: 100%; font-size: var(--fs-sm); white-space: nowrap; min-width: 1100px; }
::v-deep .kmp-table thead th {
  background: var(--warna-kepala-jadual, #1B7E91); color: #fff !important; font-weight: 600; padding: 6px 9px;
  /* !important: Vuetify's `.v-application--is-ltr .v-data-table > .v-data-table__wrapper
     > table > thead > tr > th` sets text-align:left at a far higher specificity */
  text-align: center !important; border: 1px solid #2a8ea1; vertical-align: middle;
  font-size: var(--fs-sm) !important; line-height: 1.3; position: sticky; top: 0; z-index: 4;
}
::v-deep .kmp-table thead th.sub2, ::v-deep .kmp-table thead th.sub3 { background: var(--warna-kepala-jadual-2, #15606F); font-size: var(--fs-xs) !important; }
::v-deep .kmp-table thead th.cat { left: 0; z-index: 6; }
::v-deep .kmp-table tbody td {
  padding: 8px 9px; border: 1px solid #E2E8F0; text-align: right; color: #0B1F3A; font-size: var(--fs-sm);
}
::v-deep .kmp-table tbody td.cat { text-align: left; font-weight: 600; position: sticky; left: 0; background: #fff; z-index: 2; }
::v-deep .kmp-table tbody td.kod { text-align: center; }
::v-deep .kmp-table tbody td.ket { white-space: normal; min-width: 260px; }
::v-deep .kmp-table tbody td.pct { color: #475569; }
::v-deep .kmp-table tbody tr:hover td { background: #EFF7F7 !important; }
::v-deep .kmp-table tbody tr.total td { background: var(--warna-graf, #0EA5A4) !important; color: #fff; font-weight: 700; border-color: var(--warna-graf-gelap, #0B827F); }
::v-deep .kmp-table tbody tr.total td.cat { background: var(--warna-graf, #0EA5A4) !important; color: #fff; }
::v-deep .kmp-table tbody tr.sub td { background: #F4F8FA !important; }
::v-deep .kmp-table tbody tr.sub td.cat { background: #F4F8FA !important; }
</style>
